import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getRunningTimer, stopTimer } from '../api/client';
import { Square, ChevronDown, Receipt } from 'lucide-react';
import BillingSlipModal from './BillingSlipModal';
import { useIsMobile } from '../hooks/useIsMobile';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useFeatureEnabled } from '../hooks/useFeatureEnabled';

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function TimerButton() {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const billingRecoveryEnabled = useFeatureEnabled('billing_recovery');
  const { data: currentUser } = useCurrentUser();
  const billingSlipEnabled = billingRecoveryEnabled && Boolean(currentUser?.moneta_enabled);
  const runningColor = '#dc2626';
  const runningBorder = '#fca5a5';
  const runningBackground = '#fef2f2';
  const [showDropdown, setShowDropdown] = useState(false);
  const [billingSlipOpen, setBillingSlipOpen] = useState(false);
  const [billingSlipHasDraft, setBillingSlipHasDraft] = useState(false);
  const [billingSlipAutoStart, setBillingSlipAutoStart] = useState(false);
  const [billingSlipEntryType, setBillingSlipEntryType] = useState<string>('hourly');
  const [billingSlipTimerSnapshot, setBillingSlipTimerSnapshot] = useState<{
    state: 'idle' | 'running' | 'stopped';
    elapsedSeconds: number;
  }>({ state: 'idle', elapsedSeconds: 0 });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: runningTimer } = useQuery({
    queryKey: ['running-timer'],
    queryFn: getRunningTimer,
    refetchInterval: 10000,
    staleTime: 5000,
  });

  const stopMutation = useMutation({
    mutationFn: stopTimer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['running-timer'] });
      queryClient.invalidateQueries({ queryKey: ['today-entries'] });
      queryClient.invalidateQueries({ queryKey: ['matter-time-entries'] });
    },
  });

  const runningTimerStartTime = runningTimer?.start_time;

  // Tick elapsed every second when timer is running
  useEffect(() => {
    if (!runningTimerStartTime) return;
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [runningTimer?.id, runningTimerStartTime]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const isServerTimerRunning = !!runningTimer;
  const isDraftTimerRunning = billingSlipTimerSnapshot.state === 'running';
  const isRunning = isServerTimerRunning || isDraftTimerRunning;
  const elapsed = runningTimerStartTime
    ? Math.max(0, Math.floor((nowMs - new Date(runningTimerStartTime).getTime()) / 1000))
    : billingSlipTimerSnapshot.elapsedSeconds;
  const billingSlipMounted = billingSlipOpen || billingSlipHasDraft;

  const openBillingSlip = (autoStartTimer: boolean) => {
    if (!billingSlipEnabled && !billingSlipHasDraft) return;
    setBillingSlipAutoStart(autoStartTimer && !billingSlipHasDraft);
    setBillingSlipOpen(true);
    setShowDropdown(false);
  };

  const handlePlayStop = () => {
    if (isServerTimerRunning) {
      stopMutation.mutate();
    } else if (isDraftTimerRunning) {
      // Draft timer running: just reopen the slip — timer keeps running.
      // Stop/start lives inside the slip.
      setBillingSlipOpen(true);
    } else if (billingSlipEnabled) {
      openBillingSlip(false);
    }
  };

  return (
    <>
      <div ref={dropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {/* Split button */}
        <div style={{
          display: 'flex',
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
          border: isRunning ? `1.5px solid ${runningBorder}` : '1px solid var(--border)',
          background: isRunning ? runningBackground : 'var(--card)',
          transition: 'all 0.2s ease',
        }}>
          {/* Play/Stop button */}
          <button
            onClick={handlePlayStop}
            disabled={stopMutation.isPending || (!isRunning && !billingSlipEnabled)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.3rem 0.6rem',
              border: 'none',
              background: 'transparent',
              cursor: stopMutation.isPending || (!isRunning && !billingSlipEnabled) ? 'not-allowed' : 'pointer',
              color: isRunning ? runningColor : 'var(--text-secondary)',
              opacity: !isRunning && !billingSlipEnabled ? 0.55 : 1,
              fontSize: '0.82rem',
              fontWeight: isRunning ? 700 : 500,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
            title={
              isServerTimerRunning
                ? 'Stop timer'
                : isDraftTimerRunning
                  ? 'Open billing slip (timer running)'
                  : billingSlipEnabled
                    ? 'Open billing slip'
                    : 'AI Billing Review is not enabled for your account'
            }
          >
            {isServerTimerRunning ? (
              <>
                <Square size={14} fill={runningColor} />
                <span>{isMobile ? 'Stop' : 'Stop timer'}</span>
                {!isMobile && <span>{formatDuration(elapsed)}</span>}
              </>
            ) : isDraftTimerRunning ? (
              <>
                <Receipt size={14} />
                {!isMobile && <span>Billing Slip</span>}
                <span>{formatDuration(elapsed)}</span>
              </>
            ) : (
              <>
                <Receipt size={14} />
                {!isMobile && <span>Billing Slip</span>}
              </>
            )}
          </button>

          {/* Dropdown toggle — desktop only */}
          {!isMobile && (
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              aria-label="Toggle time entries"
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.3rem 0.35rem',
                border: 'none',
                borderLeft: `1px solid ${isRunning ? runningBorder : 'var(--border)'}`,
                background: 'transparent',
                cursor: 'pointer',
                color: isRunning ? runningColor : 'var(--text-muted)',
              }}
            >
              <ChevronDown size={14} />
            </button>
          )}
        </div>

        {/* Dropdown — entry type selector */}
        {showDropdown && !isMobile && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            width: 220,
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1000,
            overflow: 'hidden',
          }}>
            <div style={{ padding: '0.4rem' }}>
              {([
                { value: 'hourly', label: 'Hourly' },
                { value: 'flat_fee_trigger', label: 'Flat Fee' },
                { value: 'contingency_internal', label: 'Contingency' },
                { value: 'expense', label: 'Expense' },
              ] as const).map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => {
                    setBillingSlipEntryType(t.value);
                    openBillingSlip(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.6rem',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    background: 'transparent',
                    color: 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    textAlign: 'left',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Receipt size={14} style={{ color: 'var(--text-muted)' }} />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {billingSlipMounted && (billingSlipEnabled || billingSlipHasDraft) && (
        <BillingSlipModal
          visible={billingSlipOpen}
          autoStartTimer={billingSlipAutoStart}
          defaultEntryType={billingSlipEntryType}
          onTimerSnapshot={setBillingSlipTimerSnapshot}
          onClose={() => {
            setBillingSlipOpen(false);
            setBillingSlipHasDraft(false);
            setBillingSlipAutoStart(false);
            setBillingSlipTimerSnapshot({ state: 'idle', elapsedSeconds: 0 });
          }}
          onHide={() => {
            setBillingSlipOpen(false);
            setBillingSlipHasDraft(true);
            setBillingSlipAutoStart(false);
          }}
        />
      )}
    </>
  );
}
